import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import classNames from 'classnames';
import MetaControl from 'Components/UI/Meta/MetaControl';
import { dispatchActionItemRefresh } from 'Hooks/useRefreshEvent';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import { openModal } from 'Helpers/ModalHelper';
import modalConsts from 'Consts/ModalNames';
import { displayErrorMessage, displaySuccess } from 'Helpers/FacetNotificationManager';

const styles = theme => ({
  root: {
    display: 'flex',
    flexDirection: 'row',
    fontSize: '12px',
    fontWeight: 500,
    boxShadow: '0 3px 6px rgba(0, 0, 0, 0.16)',
    padding: '6px',
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.common.white,
    cursor: 'pointer',
  },
  locked: {
    opacity: '0.7',
  },
  colorBar: {
    width: '6px',
    minWidth: '6px',
    marginRight: '5px',
    backgroundColor: theme.palette.common.white,
  },
  todoColor: {
    backgroundColor: theme.palette.primary.main,
  },
  inProgressColor: {
    backgroundColor: theme.palette.facet.accentBlue,
  },
  pendingColor: {
    backgroundColor: theme.palette.facet.accentBlue13,
  },
  reviewColor: {
    backgroundColor: theme.palette.facet.accentGreen7,
  },
  completedColor: {
    backgroundColor: theme.palette.secondary.main,
  },
  content: {
    display: 'flex',
    flexGrow: '1',
    flexDirection: 'column',
    color: theme.palette.facet.primaryGray,
    wordBreak: 'break-all',
    '& > div:not(:last-child)': {
      paddingBottom: theme.spacing(1),
    },
  },
  overdue: {
    color: theme.palette.facet.red,
  },
  clientRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  selectRoot: {
    fontSize: '10px !important',
    paddingBottom: '5px',
  },
  selectControlRoot: {
    paddingBottom: 0,
    width: '35%',
  },
});

const TaskCard = ({
  className,
  classes,
  client,
  name,
  dueDate,
  status,
  locked,
  resource,
  overdue,
}) => {
  const ref = useRef();
  const { logEvent, logResourceEventSuccess, logResourceEventFailure } = useEventTrackingLogEvent({ ref });
  const dueDateText = `${dueDate} ${overdue ? ' - Overdue' : ''}`;

  const handleUpdateStatus = async (newStatus) => {
    resource.attr('status', newStatus);
    try {
      await resource.submit();
      dispatchActionItemRefresh();
      displaySuccess('Updated status on task.');
      logResourceEventSuccess('Submitted Status Update on Action', resource, { newStatus });
    } catch (err) {
      displayErrorMessage(err, err.errors);
      logResourceEventFailure('Submitted Status Update on Action', resource, { newStatus });
    }
  };

  const handleClickCard = () => {
    openModal(modalConsts.taskModal, [{ actionItemLink: btoa(resource.link('self').url()) }]);
    logEvent('Clicked Task Card', { actionItemLink: resource.link('self').url() });
  };

  return (
    <div
      ref={ref}
      className={classNames(className, classes.root, {
        [classes.locked]: locked,
      })}
      role="presentation"
      onClick={handleClickCard}
    >
      <div
        className={classNames(classes.colorBar, {
          [classes.todoColor]: status === 'todo',
          [classes.inProgressColor]: status === 'in-progress',
          [classes.pendingColor]: status === 'pending',
          [classes.reviewColor]: status === 'review',
          [classes.completedColor]: status === 'completed',
        })}
      />
      <div className={classes.content}>
        <div className={classes.clientRow}>
          <div>{client}</div>
          <MetaControl
            classes={{
              root: classes.selectControlRoot,
              selectRoot: classes.selectRoot,
            }}
            helperText={null}
            attrName="status"
            resource={resource}
            onClick={e => e.stopPropagation()}
            onChange={handleUpdateStatus}
          />
        </div>
        <div>{name}</div>
        <div className={classNames({ [classes.overdue]: overdue })}>
          {dueDateText}
        </div>
      </div>
    </div>
  );
};

TaskCard.defaultProps = {
  className: '',
  dueDate: '',
};

TaskCard.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  client: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  dueDate: PropTypes.string,
  status: PropTypes.string.isRequired,
  locked: PropTypes.bool.isRequired,
  resource: PropTypes.object.isRequired,
  overdue: PropTypes.bool.isRequired,
};

export default withStyles(styles)(TaskCard);
